import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import type {
  CreateKeyResultModalProps,
  CreateKeyResultFormData,
} from './createKeyResultModal.types';
import {
  METRIC_TYPES,
  ERROR_MESSAGES,
  DEFAULT_FORM_VALUES,
} from './createKeyResultModal.definitions';
import StaticCombobox from '@/components/shared/StaticCombobox/StaticCombobox';
import CollaboratorCombobox from '@/components/shared/CollaboratorCombobox/CollaboratorCombobox';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';

/**
 * Modal for creating or editing a Key Result
 *
 * Features:
 * - Create new Key Results with full validation
 * - Edit existing Key Results (reuses same form)
 * - Dynamic form based on metric type
 * - Real-time validation and error messages
 * - Responsive layout with Tailwind CSS
 * - React Portal for proper z-index handling
 */
export default function CreateKeyResultModal({
  isOpen,
  onClose,
  onSubmit,
  objectiveId,
  objectiveTitle,
  cycleId,
  editingKeyResult,
}: CreateKeyResultModalProps) {
  const { user } = useAuth();
  const toast = useToast();

  // Initialize formData with editingKeyResult if available
  const [formData, setFormData] = useState<CreateKeyResultFormData>(() => {
    if (editingKeyResult) {
      return {
        title: editingKeyResult.title,
        description: editingKeyResult.description,
        metricType: editingKeyResult.metricType,
        unit: editingKeyResult.unit,
        initialValue: editingKeyResult.initialValue,
        targetValue: editingKeyResult.targetValue,
        currentValue: editingKeyResult.currentValue,
        startDate: editingKeyResult.startDate instanceof Date ? editingKeyResult.startDate : editingKeyResult.startDate?.toDate?.() || new Date(),
        targetDate: editingKeyResult.targetDate instanceof Date ? editingKeyResult.targetDate : editingKeyResult.targetDate?.toDate?.() || new Date(),
        responsibleId: editingKeyResult.responsibleId,
        responsibleName: editingKeyResult.responsibleName,
        responsibleEmail: editingKeyResult.responsibleEmail,
      };
    }
    return user ? {
      ...DEFAULT_FORM_VALUES,
      responsibleId: user.uid,
      responsibleName: user.displayName || '',
      responsibleEmail: user.email || '',
    } : DEFAULT_FORM_VALUES;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Convert METRIC_TYPES to combobox options (must be before conditional return)
  const metricTypeOptions = useMemo(
    () =>
      METRIC_TYPES.map((type) => ({
        value: type.value,
        label: `${type.label} - ${type.example}`,
      })),
    []
  );

  // Helper to convert date (handles both Timestamp and Date)
  const toDate = (value: any): Date => {
    if (!value) return new Date();
    // If it's a Firestore Timestamp, call toDate()
    if (value.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    // If it's already a Date, return it
    if (value instanceof Date) {
      return value;
    }
    // Fallback: try to create a Date from it
    return new Date(value);
  };

  // Update formData if user loads after component mount (only for create mode)
  useEffect(() => {
    if (!editingKeyResult && isOpen && user && !formData.responsibleId) {
      setFormData((prev) => ({
        ...prev,
        responsibleId: user.uid,
        responsibleName: user.displayName || '',
        responsibleEmail: user.email || '',
      }));
    }
  }, [editingKeyResult, isOpen, user, formData.responsibleId]);

  /**
   * Validate form data before submission
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = ERROR_MESSAGES.TITLE_REQUIRED;
    } else if (formData.title.length < 10) {
      newErrors.title = ERROR_MESSAGES.TITLE_MIN_LENGTH;
    } else if (formData.title.length > 100) {
      newErrors.title = ERROR_MESSAGES.TITLE_MAX_LENGTH;
    }

    // Description validation
    if (formData.description && formData.description.length > 500) {
      newErrors.description = ERROR_MESSAGES.DESCRIPTION_MAX_LENGTH;
    }

    // Metric type validation
    if (!formData.metricType) {
      newErrors.metricType = ERROR_MESSAGES.METRIC_TYPE_REQUIRED;
    }

    // Value validation
    if (formData.initialValue === formData.targetValue) {
      newErrors.targetValue = ERROR_MESSAGES.VALUES_MUST_DIFFER;
    }

    // Percentage range validation
    if (formData.metricType === 'percentage') {
      if (formData.initialValue < 0 || formData.initialValue > 100) {
        newErrors.initialValue = ERROR_MESSAGES.PERCENTAGE_RANGE;
      }
      if (formData.targetValue < 0 || formData.targetValue > 100) {
        newErrors.targetValue = ERROR_MESSAGES.PERCENTAGE_RANGE;
      }
    }

    // Date validation
    if (!formData.startDate) {
      newErrors.startDate = ERROR_MESSAGES.START_DATE_REQUIRED;
    }
    if (!formData.targetDate) {
      newErrors.targetDate = ERROR_MESSAGES.TARGET_DATE_REQUIRED;
    }
    if (formData.startDate && formData.targetDate && formData.startDate >= formData.targetDate) {
      newErrors.targetDate = ERROR_MESSAGES.DATE_ORDER;
    }

    // Responsible validation
    if (!formData.responsibleId) {
      newErrors.responsibleId = ERROR_MESSAGES.RESPONSIBLE_REQUIRED;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, revise as informações do formulário');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      console.error('Error submitting key result:', error);

      // Handle specific business rule errors
      if (error.message?.includes('max-kr-exceeded')) {
        setErrors({ form: ERROR_MESSAGES.MAX_KR_EXCEEDED });
      } else if (error.message?.includes('invalid-objective-status')) {
        setErrors({ form: ERROR_MESSAGES.INVALID_OBJECTIVE_STATUS });
      } else {
        setErrors({ form: error.message || 'Erro ao salvar Key Result' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle input changes with validation
   */
  const handleChange = (
    field: keyof CreateKeyResultFormData,
    value: string | number | Date
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="font-montserrat text-xl font-semibold text-gray-900">
              {editingKeyResult ? 'Editar Key Result' : 'Criar Key Result'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Objetivo: {objectiveTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={clsx(
                  'mt-1 block w-full rounded-lg border px-4 py-2 font-montserrat text-sm transition-colors',
                  errors.title
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                )}
                placeholder="Ex: Aumentar receita recorrente mensal"
                disabled={isSubmitting}
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Descrição
              </label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className={clsx(
                  'mt-1 block w-full rounded-lg border px-4 py-2 font-montserrat text-sm transition-colors',
                  errors.description
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                )}
                placeholder="Descrição opcional do Key Result"
                disabled={isSubmitting}
              />
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
            </div>

            {/* Metric Type */}
            <div>
              <label htmlFor="metricType" className="block text-sm font-medium text-gray-700">
                Tipo de Métrica <span className="text-red-500">*</span>
              </label>
              <div className={clsx('mt-1', errors.metricType && 'rounded-lg border-2 border-red-500')}>
                <StaticCombobox
                  options={metricTypeOptions}
                  selectedValue={formData.metricType}
                  onChange={(value) => handleChange('metricType', value || 'number')}
                  disabled={isSubmitting}
                  placeholder="Selecione o tipo de métrica"
                />
              </div>
              {errors.metricType && <p className="mt-1 text-sm text-red-600">{errors.metricType}</p>}
            </div>

            {/* Unit (conditional - only for number and currency) */}
            {(formData.metricType === 'number' || formData.metricType === 'currency') && (
              <div>
                <label htmlFor="unit" className="block text-sm font-medium text-gray-700">
                  Unidade {formData.metricType === 'currency' && '(opcional)'}
                </label>
                <input
                  id="unit"
                  type="text"
                  value={formData.unit || ''}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 font-montserrat text-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder={
                    formData.metricType === 'currency' ? 'Ex: milhões' : 'Ex: usuários, vendas, downloads'
                  }
                  disabled={isSubmitting}
                />
              </div>
            )}

            {/* Initial Value & Target Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="initialValue" className="block text-sm font-medium text-gray-700">
                  Valor Inicial <span className="text-red-500">*</span>
                </label>
                <input
                  id="initialValue"
                  type="number"
                  step={formData.metricType === 'currency' ? '0.01' : '1'}
                  value={formData.initialValue}
                  onChange={(e) => handleChange('initialValue', parseFloat(e.target.value) || 0)}
                  className={clsx(
                    'mt-1 block w-full rounded-lg border px-4 py-2 font-montserrat text-sm transition-colors',
                    errors.initialValue
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  )}
                  disabled={isSubmitting}
                />
                {errors.initialValue && <p className="mt-1 text-sm text-red-600">{errors.initialValue}</p>}
              </div>

              <div>
                <label htmlFor="targetValue" className="block text-sm font-medium text-gray-700">
                  Valor Alvo <span className="text-red-500">*</span>
                </label>
                <input
                  id="targetValue"
                  type="number"
                  step={formData.metricType === 'currency' ? '0.01' : '1'}
                  value={formData.targetValue}
                  onChange={(e) => handleChange('targetValue', parseFloat(e.target.value) || 0)}
                  className={clsx(
                    'mt-1 block w-full rounded-lg border px-4 py-2 font-montserrat text-sm transition-colors',
                    errors.targetValue
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  )}
                  disabled={isSubmitting}
                />
                {errors.targetValue && <p className="mt-1 text-sm text-red-600">{errors.targetValue}</p>}
              </div>
            </div>

            {/* Start Date & Target Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                  Data de Início <span className="text-red-500">*</span>
                </label>
                <input
                  id="startDate"
                  type="date"
                  value={formData.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleChange('startDate', new Date(e.target.value))}
                  className={clsx(
                    'mt-1 block w-full rounded-lg border px-4 py-2 font-montserrat text-sm transition-colors',
                    errors.startDate
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  )}
                  disabled={isSubmitting}
                />
                {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
              </div>

              <div>
                <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700">
                  Data Alvo <span className="text-red-500">*</span>
                </label>
                <input
                  id="targetDate"
                  type="date"
                  value={formData.targetDate.toISOString().split('T')[0]}
                  onChange={(e) => handleChange('targetDate', new Date(e.target.value))}
                  className={clsx(
                    'mt-1 block w-full rounded-lg border px-4 py-2 font-montserrat text-sm transition-colors',
                    errors.targetDate
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  )}
                  disabled={isSubmitting}
                />
                {errors.targetDate && <p className="mt-1 text-sm text-red-600">{errors.targetDate}</p>}
              </div>
            </div>

            {/* Responsible */}
            <div>
              <label htmlFor="responsible" className="block text-sm font-medium text-gray-700">
                Responsável <span className="text-red-500">*</span>
              </label>
              <div className={clsx(errors.responsibleId && 'rounded-lg border-2 border-red-500')}>
                <CollaboratorCombobox
                  selectedId={formData.responsibleId}
                  onChange={(collaborator: CollaboratorPublic | null) => {
                    if (collaborator) {
                      setFormData((prev) => ({
                        ...prev,
                        responsibleId: collaborator.id,
                        responsibleName: collaborator.name,
                        responsibleEmail: collaborator.email || '',
                      }));
                      // Clear error when valid selection is made
                      if (errors.responsibleId) {
                        setErrors((prev) => {
                          const { responsibleId: _, ...rest } = prev;
                          return rest;
                        });
                      }
                    }
                  }}
                  disabled={isSubmitting}
                />
              </div>
              {errors.responsibleId && <p className="mt-1 text-sm text-red-600">{errors.responsibleId}</p>}
            </div>
          </div>

          {/* Form-level error */}
          {errors.form && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-800">{errors.form}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 font-montserrat text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={clsx(
              'rounded-lg px-6 py-2 font-montserrat text-sm font-medium text-white transition-colors',
              isSubmitting
                ? 'cursor-not-allowed bg-blue-400'
                : 'bg-blue-600 hover:bg-blue-700'
            )}
          >
            {isSubmitting
              ? 'Salvando...'
              : editingKeyResult
              ? 'Atualizar Key Result'
              : 'Criar Key Result'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
